import { useState, type ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { ProtectedRoute } from '../components/auth/index.js';
import { LoadState } from '../components/features/org-issues/index.js';
import { CreateSeriesForm, SeriesCards } from '../components/features/team-meetings/index.js';
import { useTeamMeetingSeriesList } from '../hooks/useTeamMeetings.js';

/**
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG のトップ。自分が見られるシリーズを並べ、開催回の画面へ移る入口と、シリーズを作る入口を置く。MCP（org-issues の team_mtg_*）と同じ API を叩く
 * @graph-connects useTeamMeetingSeriesList [calls] 見られるシリーズの一覧
 * @graph-connects SeriesCards [calls] シリーズの一覧
 * @graph-connects CreateSeriesForm [calls] シリーズの作成
 */
export const Route = createFileRoute('/team-meetings/')({
  component: TeamMeetingsRoute,
});

/** @graph-connects ProtectedRoute [calls] ページアクセス可否を判定しガードする */
function TeamMeetingsRoute(): ReactNode {
  return (
    <ProtectedRoute pageKey="teamMeetings">
      <TeamMeetingsPage />
    </ProtectedRoute>
  );
}

/** @graph-connects none */
function TeamMeetingsPage(): ReactNode {
  const { t } = useTranslation();
  const list = useTeamMeetingSeriesList();
  const [composing, setComposing] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-[var(--color-text-primary)]">
            {t('teamMeetings.title')}
          </h1>
          <p className="mt-1 text-sm text-[var(--color-text-muted)]">{t('teamMeetings.intro')}</p>
        </div>
        {!composing && list.data !== undefined && (
          <Button
            type="button"
            size="sm"
            className="shrink-0"
            onClick={() => {
              setComposing(true);
            }}
          >
            {t('teamMeetings.create.open')}
          </Button>
        )}
      </div>
      {composing && list.data !== undefined && (
        <CreateSeriesForm
          candidates={list.data.ownerCandidates}
          onCancel={() => {
            setComposing(false);
          }}
        />
      )}
      <LoadState
        isLoading={list.isLoading}
        isError={list.isError}
        onRetry={() => {
          void list.refetch();
        }}
      />
      {list.data !== undefined && <SeriesCards series={list.data.series} />}
    </div>
  );
}
